import {
  applyEdgeResistance,
  decideSwipe,
  DRAG_START_PX
} from 'components/RebindingTableView/swipeCommit';

/**
 * The trackpad/mouse table-swipe DECISION machine, extracted from
 * useTrackpadTableSwipe so the hard part — one commit per physical gesture,
 * inertia-tail swallowing, and the three-signal re-arm — is a pure function a
 * test can drive event by event (review ask on PR #3721: the hook combined
 * gesture logic, Observer wiring and cleanup in one 350-line body, and only
 * the sign helper was testable).
 *
 * The hook keeps everything environmental: the GSAP Observer, the pointer
 * blocking, the click swallow, teardown. This module owns everything that
 * DECIDES, and holds no module state — the caller threads `SwipeGestureState`
 * through each call.
 */

export type Neighbours = { prev: string | null; cur: string; next: string | null };

/**
 * Sign that maps an Observer delta onto the track's offset, per input.
 *
 * WHEEL reports scroll semantics: two fingers moving left emit POSITIVE
 * deltaX ("scroll the content left"), so the track must move by -deltaX.
 * POINTER reports raw movement: dragging left emits NEGATIVE deltaX, and the
 * felt must follow the hand — the track moves by +deltaX. Getting this wrong
 * inverted mouse dragging on day one (Jason on test, 8/20): dragging the
 * table left paged to the PREVIOUS table.
 */
export const gestureDeltaSign = (eventType: string): 1 | -1 =>
  eventType.startsWith('pointer') || eventType.startsWith('mouse') ? 1 : -1;

/**
 * A committed gesture swallows its own inertia tail — but a tail is a
 * CONTINUOUS ~60-90Hz stream. Any gap longer than this means the fingers
 * left the pad and came back: a new physical gesture, even in the SAME
 * direction. This is what lets a player flick table 1 → 4 as three quick
 * same-direction swipes (Jason 8/20) while a single hard flick still pages
 * exactly once.
 */
export const SAME_GESTURE_LULL_MS = 90;

/**
 * A lull alone is not enough to re-arm: an inertia tail DIES with sparse,
 * sub-pixel crumbs whose gaps also exceed the lull — and round 3's lull-only
 * rule let those crumbs re-arm, grab the fresh settle, and inject micro-drags:
 * the post-settle left-right jitter of the felt and the dot, plus the
 * phase-churn "pulse" on the dock tiles (Jason 8/21). A real new flick OPENS
 * with a decisive delta; crumbs never reach this.
 */
export const NEW_BURST_MIN_DELTA_PX = 4;

/**
 * ...and a decisive delta is not enough either: Magic Mouse / trackpad tails
 * can end on a single CHUNKY hiccup — one late delta past the size bar after a
 * lull-length gap — which passed both round-4 gates, re-armed, grabbed the
 * settle and shimmied the felt a few px before springing back: the
 * trackpad-only post-switch jitter of the table + action rail (Jason 8/21;
 * dock dots were already fixed by the size bar). The discriminator that a
 * hiccup can never fake is SPEED: Observer's tracked velocity on a real new
 * flick is hundreds→thousands of px/s from its opening events, while a dying
 * tail's is near zero by the time a lull has passed. 250 px/s is ~half the
 * gentlest deliberate swipe (≈50 px in 100 ms) and ~5× a chunky crumb
 * (a few px over a >90 ms window). Worst case the gate defers a real re-arm by
 * one wheel event (~8–16 ms) while the tracker catches up — imperceptible.
 * `velocityX` is already load-bearing for wheel input: decideSwipe commits on
 * it every gesture.
 */
export const NEW_BURST_MIN_VELOCITY_PX_S = 250;

/** Accumulated travel and latches for the gesture currently on the wire. */
export interface SwipeGestureState {
  offset: number;
  dragging: boolean;
  committed: boolean;
  /**
   * Sign of the travel that committed — inertia decays but NEVER reverses,
   * so an opposite-sign delta after a commit is a NEW physical gesture.
   */
  committedSign: number;
  /**
   * The caller turned this gesture down (a committed switch is settling).
   * Latched so the rest of the momentum tail is swallowed rather than asking
   * again every frame and starting a drag halfway through.
   */
  refused: boolean;
  lastVelocity: number;
  lastDeltaAt: number;
}

export const initialSwipeGestureState = (): SwipeGestureState => ({
  offset: 0,
  dragging: false,
  committed: false,
  committedSign: 0,
  refused: false,
  lastVelocity: 0,
  lastDeltaAt: 0
});

/** One Observer delta, reduced to what the machine reasons about. */
export interface SwipeDeltaInput {
  eventType: string;
  deltaX: number;
  /** Observer's tracked velocity in px/s (0 when unavailable). */
  velocityXPxPerS: number;
  nowMs: number;
}

export type SwipeGestureAction =
  | { kind: 'none' }
  | { kind: 'move'; offsetPx: number }
  | { kind: 'commit'; targetId: string | null; velocityPxPerMs: number };

/**
 * Feed one delta through the gesture machine. Mutates and returns `state`
 * (single-owner mutable state is exactly how the hook used these locals; the
 * tests own their copy). `requestStart` is the caller's accept/refuse gate —
 * it returns the carry offset for a grabbed spring-back, or null to refuse.
 */
export const stepSwipeGesture = (
  state: SwipeGestureState,
  input: SwipeDeltaInput,
  neighbours: Neighbours | null,
  viewportWidth: number,
  requestStart: (session: Neighbours) => number | null
): SwipeGestureAction => {
  const sign = gestureDeltaSign(input.eventType);
  const gapMs = state.lastDeltaAt === 0 ? Infinity : input.nowMs - state.lastDeltaAt;
  state.lastDeltaAt = input.nowMs;

  // Already decided (or refused) — swallow the rest of this gesture's
  // momentum tail so it can neither page again nor drag the settled table
  // back. TWO signals mark a NEW physical gesture and re-arm instead:
  //   • the delta's sign flipped — an inertia tail is strictly monotonic
  //     in sign, so a reversal can only be the player (the
  //     swipe-left-then-right that used to be eaten whole; clicking only
  //     "fixed" it because macOS kills inertia on click), or
  //   • a lull in the stream — a tail fires continuously at ~60-90Hz, so
  //     a gap means fingers lifted and returned: the same-direction
  //     table-1→4 triple-flick (Jason 8/20).
  // The settle either lands on is grabbable now (see beginSwipeDrag).
  if (state.committed || state.refused) {
    const dir = Math.sign(sign * input.deltaX);
    const mag = Math.abs(input.deltaX);
    const speed = Math.abs(input.velocityXPxPerS);
    const reversed =
      state.committed && dir !== 0 && state.committedSign !== 0 && dir !== state.committedSign;
    // Same-direction re-arm takes all three: a gap (fingers left the
    // pad), a sized delta (not a sub-pixel crumb) AND real speed (not a
    // tail's one chunky hiccup) — see the constants' comments.
    //
    // A REFUSED gesture re-arms on the same evidence. It used to require
    // `committed`, so a refusal could only ever be cleared by Observer's
    // onStop: start a wheel gesture during a ~260ms programmatic tap-slide
    // and the caller's refusal was held for as long as the inertia ran —
    // up to a second of swallowed input, the exact dead-window class this
    // work set out to kill (SQE scan, Marlon 8/24). A refusal carries no
    // committedSign, so reversal cannot be judged for one; the three-signal
    // burst is the evidence that applies, and requiring real hand speed is
    // what keeps a dying tail from passing it.
    const newBurst =
      (state.committed || state.refused) &&
      gapMs > SAME_GESTURE_LULL_MS &&
      mag >= NEW_BURST_MIN_DELTA_PX &&
      speed >= NEW_BURST_MIN_VELOCITY_PX_S;
    if (reversed || newBurst) {
      state.offset = 0;
      state.dragging = false;
      state.committed = false;
      state.refused = false;
      state.committedSign = 0;
    } else {
      return { kind: 'none' };
    }
  }

  if (!neighbours || (!neighbours.prev && !neighbours.next)) return { kind: 'none' };

  // Per-input sign: wheel is scroll-semantics, pointer is raw movement —
  // see gestureDeltaSign.
  state.offset += sign * input.deltaX;
  state.lastVelocity = (sign * input.velocityXPxPerS) / 1000; // px/s → px/ms

  if (!state.dragging) {
    if (Math.abs(state.offset) < DRAG_START_PX) return { kind: 'none' };
    const carry = requestStart(neighbours);
    if (carry === null) {
      state.refused = true;
      return { kind: 'none' };
    }
    state.dragging = true;
    // Grabbed a spring-back in flight: this gesture's travel continues
    // from where the felt is, not from a panel boundary.
    state.offset += carry;
  }

  // COMMIT THE INSTANT THE GESTURE IS DECIDED, not when momentum finally
  // dies. `onStop` cannot fire until wheel events cease, and a trackpad's
  // inertia keeps them coming for up to a second after the fingers lift —
  // so waiting for it meant the felt kept travelling on momentum and the
  // switch landed long after the player had finished the gesture. That
  // lag is what read as unresponsive and out of step with the trackpad
  // (Jason 2026-08-16). Deciding mid-gesture also starts the settle from
  // where the player actually let go.
  const decision = decideSwipe(
    state.offset,
    state.lastVelocity,
    viewportWidth,
    Boolean(neighbours.prev),
    Boolean(neighbours.next)
  );
  if (decision.direction !== 0) {
    state.committed = true;
    state.committedSign = Math.sign(state.offset);
    const targetId = decision.direction === 1 ? neighbours.next : neighbours.prev;
    return { kind: 'commit', targetId, velocityPxPerMs: state.lastVelocity };
  }

  const blocked = (state.offset < 0 && !neighbours.next) || (state.offset > 0 && !neighbours.prev);
  return {
    kind: 'move',
    offsetPx: blocked ? applyEdgeResistance(state.offset, viewportWidth) : state.offset
  };
};

/**
 * The stream went quiet without a decision — a short nudge, or an edge pull
 * that never cleared the bar. Returns whether the caller owes a spring-back
 * (a committing gesture already returned from stepSwipeGesture) and resets
 * the state for the next gesture.
 */
export const stopSwipeGesture = (state: SwipeGestureState): { springBack: boolean } => {
  const springBack = state.dragging && !state.committed;
  Object.assign(state, initialSwipeGestureState());
  return { springBack };
};
